import { useEffect } from 'react';

interface KeyboardAction {
	key: string;
	action: () => void;
}

/**
 * Dynamic keyboard navigation hook that accepts an array of key-action pairs.
 *
 * @example
 * ```tsx
 * useKeyboardNavigation([
 *   { key: 'ArrowLeft', action: moveToPrevious },
 *   { key: 'ArrowRight', action: moveToNext },
 *   { key: 'Escape', action: closeModal },
 *   { key: 'Enter', action: submitForm },
 * ]);
 * ```
 *
 * @param actions - Array of objects with key and action function
 */
export const useKeyboardNavigation = (actions: KeyboardAction[]) => {
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			// skip keyboard navigation if the event target is an input field
			const target = event.target as HTMLElement;
			if (
				target &&
				(target.tagName === 'INPUT' ||
					target.tagName === 'TEXTAREA' ||
					target.contentEditable === 'true')
			) {
				return;
			}

			// Prevent default scroll behavior for keys that commonly cause scrolling
			const scrollKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
			if (scrollKeys.includes(event.code)) {
				event.preventDefault();
			}

			event.stopPropagation(); // prevents the page from scrolling when navigation pressing keys
			const action = actions.find(({ key }) => event.code === key);
			if (action) {
				action.action();
			}
		};

		document.body.addEventListener('keydown', handleKeyDown);

		return () => {
			document.body.removeEventListener('keydown', handleKeyDown);
		};
	}, [actions]);
};
